import React, {ReactElement, useState} from 'react';
import {withTranslation, useTranslation} from 'react-i18next';
import AuthUtils from '../../../utils/AuthUtils';
import {
  Button,
  // Card,
  // CardContent,
  FormControl,
  FormHelperText,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  Link,
  LinearProgress,
  IconButton,
  InputAdornment
} from '@material-ui/core';
import {useForm, Controller} from 'react-hook-form';
import {useSnackbar} from 'notistack';
import {connect, useDispatch, useSelector} from 'react-redux';
// other imports
import NavRoutes from '../../../constants/route/NavRoutes';
import {SnackBarConfig} from '../../../utils/SnackBarConfig';
import {BASE_ROUTE_ADMIN} from 'src/constants/route/BaseRoute';

// shared imports
import {loginService} from '@shared/services/PublicService';
import {validateWithHookForm} from '@shared/modules/public/login/Login';
import {
  _login,
  _userType,
  _groupId,
  _userGroups,
  _showMessageAlert,
  _setDefaultTabAfterAuth,
  _setDefaultGroupAfterAuth,
  _setVerifyOTPUserDetails
} from '@shared/store/actions/AuthActions';
import InitSharedRepo from '../../../../../shared';

// scss import
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import './Login.scss';
import {FONT_SECONDARY_COLOR, APP, GET_8_PIXEL_BOX} from 'src/style/variable';
import MedopsLogo from '@components/system/MyIcons/MedopsLogo';
import {Visibility, VisibilityOff} from '@material-ui/icons';
import LogoIcon from './LogoIcon';
import {State} from '@shared/store/reducers/Index';
import {SYSTEM_MODULES} from '@shared/constants/Mapping';
import {consumePostLoginRedirect} from 'src/utils/PostLoginRedirect';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    signInBox: {
      display: 'flex',
      justifyContent: 'center',
      width: '50%',
      flexDirection: 'column',
      '@media (min-width: 0px) and (max-width: 1023px)': {
        width: '100%'
      }
    },
    register: {
      marginTop: '25px',
      // width: '-webkit-fill-available',
      width: '100%',
      justifyContent: 'center',
      '@media (min-height: 0px) and (max-height: 499px)': {
        marginTop: '5px'
      }
    },
    FormLabel: {
      left: '-0.8rem',
      fontSize: '1.2rem',
      color: FONT_SECONDARY_COLOR
    },
    appBar: {
      color: theme.palette.text.primary,
      background: theme.palette.background.paper,
      boxShadow: 'none',
      [theme.breakpoints.up('md')]: {
        width: `100%`
      }
    },
    loader: {
      borderTopRightRadius: 5,
      position: 'absolute',
      width: '100%',
      top: 0
    },
    loginButton: {
      padding: `${GET_8_PIXEL_BOX(1)} 0`,
      fontFamily: APP.FONT_FAMILY.BOLD,
      height: '2.7rem'
    },
    copyRightText: {
      fontSize: '1rem'
    },
    emailId: {
      fontWeight: 'bold'
    },
    password: {
      fontWeight: 'bold'
    },
    logo: {
      '@media (min-width: 0px) and (max-width: 1023px) and (min-height: 500px)': {
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: '40px'
      },
      '@media (min-height: 0px) and (max-height: 499px)': {
        display: 'flex',
        justifyContent: 'center',
        paddingBottom: '0px'
      },
      '@media (min-width: 1024px)': {
        display: 'none'
      }
    }
  })
);
let Login = (props: any): ReactElement => {
  const classes = useStyles();
  const {history} = props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [formState, setFormState] = useState({
    loading: false,
    showPassword: false
  });

  // validation
  const {control, handleSubmit, errors} = useForm({
    mode: 'all',
    resolver: async values => {
      return {values, errors: validateWithHookForm(values)};
    }
  });

  const defaultTabAfterAuth = useSelector(
    (state: State) => state.auth.defaultTabAfterAuth
  );

  const dispatch = useDispatch();

  const handleAuthRedirect = (token: any, type: string): void => {
    if (token) {
      InitSharedRepo.initAfterAuth({token});
      AuthUtils.setAdminAuthToken({token, portal: BASE_ROUTE_ADMIN});
      if (typeof props?._login === 'function') {
        const postLoginRedirect = consumePostLoginRedirect();
        if (postLoginRedirect?.route) {
          if (postLoginRedirect.deepLink?.clinic_group_id) {
            dispatch(
              _setDefaultGroupAfterAuth({
                clinic_group_id: postLoginRedirect.deepLink.clinic_group_id
              })
            );
          }
          props._login(token, null, postLoginRedirect.route);
        } else if (defaultTabAfterAuth === SYSTEM_MODULES.PRIOR_AUTHORIZATIONS) {
          dispatch(
            _setDefaultTabAfterAuth({
              tab: null
            })
          );
          const BASE_ROUTE = `${BASE_ROUTE_ADMIN}/${NavRoutes.PRIVATE.HOME}`;
          const priorAuthURL = `${BASE_ROUTE}/${NavRoutes.PRIVATE.ADMIN.PRIOR_AUTHORIZATIONS}`;
          props._login(token, null, priorAuthURL);
        } else {
          props._login(token, null, BASE_ROUTE_ADMIN);
        }
        props._showMessageAlert(true);
      }
    }
    if (type === BASE_ROUTE_ADMIN) {
      AuthUtils.setAdminAuthToken({token, portal: BASE_ROUTE_ADMIN});
      props._adminLogin(token, BASE_ROUTE_ADMIN);
    }
  };

  const handleFormSubmit = async (values: any) => {
    setFormState({...formState, loading: true});
    try {
      const loginDetails = await loginService(values);
      if (loginDetails?.requires2fa) {
        InitSharedRepo.initAfterAuth({token: loginDetails?.token});
        AuthUtils.setAdminAuthToken({
          token: loginDetails?.token,
          portal: BASE_ROUTE_ADMIN
        });
        dispatch(
          _setVerifyOTPUserDetails({
            user: loginDetails?.user ?? null
          })
        );
        history.push(`/${NavRoutes.PUBLIC.VERIFY_OTP}`);
      } else {
        handleAuthRedirect(loginDetails.token, '');
      }
      setFormState({...formState, loading: false});
    } catch (e: any) {
      enqueueSnackbar(
        e && e.message ? e.message : t('APP.ERROR'),
        SnackBarConfig('e')
      );
      setFormState({...formState, loading: false});
    }
  };

  const redirectToForgotPassword = () => {
    history.push(`/${NavRoutes.PUBLIC.FORGOT_PASSWORD}`);
  };

  /**
   * COMMENTED REDIRECT TO CREATING PRACTICE FUNCTION
   */
  // const redirectToRegister = () => {
  //   history.push(`/${NavRoutes.PUBLIC.REGISTER}`);
  // };

  const renderLogo = (): ReactElement => {
    return (
      <div className={classes.logo}>
        <MedopsLogo style={{height: '100%', width: '30%'}} />
      </div>
    );
  };

  // Hide or Show Password
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const handleClickShowPassword = () => {
    if (isPasswordVisible == true) {
      setIsPasswordVisible(false);
    } else {
      setIsPasswordVisible(true);
    }
  };

  return (
    <div
      className={['login-wrapper center-children'].join(' ')}
      data-test-id="pcl-login-loginPage">
      {formState.loading && (
        <LinearProgress className={classes.loader} color="primary" />
      )}
      <div className="login-content-wrap">
        <LogoIcon />
        <div className={classes.signInBox}>
          {renderLogo()}
          <div className="card-wrapper">
            <div className="card-content">
              <div className="login-form">
                <Typography className="login-title input-header" variant={'h3'}>
                  {t('LOGIN.SIGNIN')}
                </Typography>
                <Typography
                  className="login-subtitle input-header"
                  variant={'subtitle1'}>
                  {t('LOGIN.SUB_TITLE')}
                </Typography>
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                  <div className={classes.emailId}>{t('LOGIN.EMAIL_ID')}</div>
                  <FormControl
                    error={!!errors.email}
                    fullWidth={true}
                    variant="outlined">
                    <Controller
                      id="email"
                      as={StyledOutlinedInput}
                      control={control}
                      margin={'dense'}
                      name={'email'}
                      type={'text'}
                      value={'email'}
                      autoComplete="on"
                      placeholder={t('LOGIN.EMAIL')}
                      defaultValue=""
                      error={!!errors.email}
                    />
                    {errors.email && (
                      <FormHelperText>{t(errors.email)}</FormHelperText>
                    )}
                  </FormControl>
                  <div className={classes.password}>
                    {t('LOGIN.LOGIN_PASSWORD')}
                  </div>
                  <FormControl
                    error={!!errors.password}
                    fullWidth
                    variant="outlined">
                    <Controller
                      id="password"
                      as={StyledOutlinedInput}
                      control={control}
                      margin={'dense'}
                      name={'password'}
                      type={isPasswordVisible ? 'text' : 'password'}
                      autoComplete="on"
                      placeholder={t('LOGIN.PASSWORD')}
                      defaultValue=""
                      error={errors.password ? true : false}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            data-test-id="pcl-login-forgotPasswordLink">
                            {isPasswordVisible ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    {errors.password && (
                      <FormHelperText>{t(errors.password)}</FormHelperText>
                    )}
                  </FormControl>
                  <div className="forgot-p-link">
                    <Typography className="forgot" variant={'subtitle1'}>
                      <Link
                        type={'button'}
                        component={'button'}
                        onClick={redirectToForgotPassword}>
                        {t('LOGIN.FORGOT_PASSWORD') + '?'}
                      </Link>
                    </Typography>
                  </div>
                  <Button
                    fullWidth
                    variant="contained"
                    size={'medium'}
                    data-test-id="pcl-login-signInButton"
                    color="primary"
                    className={[classes.loginButton].join(' ')}
                    type={'submit'}
                    disabled={formState.loading}
                    onClick={handleSubmit(handleFormSubmit)}>
                    {t('LOGIN.LOGIN_BUTTON_TEXT')}
                  </Button>

                  {/* COMMENTED: CREATE PRACTICE GROUP LINK */}
                  {/* <Link
                    type={'button'}
                    className={classes.register}
                    component={'button'}
                    onClick={redirectToRegister}>
                    {t('LOGIN.SIGN_UP')}
                  </Link> */}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
// Connecting component with redux state
// @ts-ignore
Login = connect(
  () => ({}),
  {
    _login,
    _userType,
    _groupId,
    _userGroups,
    _showMessageAlert
  }
  // @ts-ignore
)(Login);

// @ts-ignore
export default withTranslation()(Login);
